<template>
  <div class="container">
    <!-- Header Section with Add, Search, and Reset buttons -->
    <div class="header">
      <button class="btn btn-add" @click="openCreateModal">+ Add</button>
      <div class="header-right">
        <input 
          type="text" 
          placeholder="Search groups..." 
          v-model="searchQuery"
          class="search-input"
        />
        <button class="btn btn-search" @click="handleSearch">Search</button>
        <button class="btn btn-reset" @click="handleReset">↻ Reset</button>
      </div>
    </div>

    <!-- Table Section -->
    <div class="table-wrapper">
      <table class="data-table">
        <thead>
          <tr>
            <th><input type="checkbox" /></th>
            <th>User Account</th>
            <th>Group Name</th>
            <th>Notes</th>
            <th>Creation Time</th>
            <th>Operation</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginatedData" :key="item.id">
            <td><input type="checkbox" /></td>
            <td>{{ item.userAccount }}</td>
            <td class="group-name">{{ item.groupName }}</td>
            <td class="notes">{{ item.notes }}</td>
            <td>{{ item.creationTime }}</td>
            <td class="operations">
              <button class="btn btn-assign" @click="() => openAssignModal(item)">Assign</button>
              <button class="btn btn-config" @click="() => openAdsConfig(item)">Ads Configuration</button>
              <button class="btn btn-delete" @click="deleteAdGroup(item)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Section -->
    <div class="pagination">
      <span class="total-info">Total {{ totalItems }}</span>
      <div class="pagination-controls">
        <button class="btn btn-nav" @click="previousPage" :disabled="currentPage === 1">‹</button>
        <button class="btn btn-page-num active">{{ currentPage }}</button>
        <button class="btn btn-nav" @click="nextPage" :disabled="currentPage >= totalPages">›</button>
        <span class="go-to">Go to</span>
        <input 
          type="number" 
          v-model.number="goToPage" 
          @keyup.enter="handleGoTo"
          placeholder="1"
          class="go-to-input"
          min="1"
          :max="totalPages"
        />
      </div>
    </div>

    <!-- Ads Configuration Modal -->
    <div v-if="showAdsModal" class="modal-overlay ads-config-overlay" @click.self="closeAdsConfig">
      <div class="modal-content ads-config-modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="modal-title-container">
            <h2 class="modal-title">Ads Configuration - {{ selectedGroup.groupName }}</h2>
            <span v-if="isAdmin" class="admin-badge">Admin</span>
          </div>
          <button class="close-btn" @click="closeAdsConfig">×</button>
        </div>

        <!-- Tabs -->
        <div class="tabs-container">
          <button 
            class="tab" 
            :class="{ active: activeTab === 'screen' }"
            @click="activeTab = 'screen'"
          >
            <i class="el-icon-monitor"></i> Screen Ads ({{ screenAds.length }})
          </button>
          <button 
            class="tab" 
            :class="{ active: activeTab === 'banner' }"
            @click="activeTab = 'banner'"
          >
            <i class="el-icon-picture"></i> Banner Ads ({{ bannerAds.length }})
          </button>
          <button class="btn btn-add-ads" @click="openSelectAdsModal">+ Add {{ activeTab === 'screen' ? 'Screen' : 'Banner' }} Ads</button>
        </div>

        <!-- Content Area -->
        <div class="modal-body">
          <div v-if="configLoading" class="config-loading">Loading configuration...</div>
          <template v-else>
          <div v-if="activeTab === 'screen'" class="ads-list">
            <div v-if="screenAds.length === 0" class="empty-state">
              <p>No Screen ads in queue</p>
              <a href="#" class="add-link" @click.prevent="openSelectAdsModal">Add from gallery</a>
            </div>
            <template v-else>
              
              <div 
                v-for="(ad, index) in screenAds" 
                :key="ad.id"
                class="ad-item"
                draggable="true"
                @dragstart="dragStart($event, 'screen', index)"
                @dragover="dragOver($event)"
                @drop="drop($event, 'screen', index)"
                @dragend="dragEnd"
              >
                <div class="ad-number">#{{ index + 1 }}</div>
                <div class="drag-handle">
                  <div class="dot-row">• •</div>
                  <div class="dot-row">• •</div>
                  <div class="dot-row">• •</div>
                </div>
                <img
                  v-if="ad.mediaType !== 'video'"
                  :src="ad.image"
                  :alt="ad.name"
                  class="ad-image"
                  @error="handleAdImageError"
                />
                <video
                  v-else
                  :src="ad.image"
                  class="ad-image ad-image-video"
                  muted
                  playsinline
                  autoplay
                  loop
                />
                <div class="ad-info">
                  <h4 class="ad-name">{{ ad.name }}</h4>
                  <p class="ad-brand">{{ ad.brand }}</p>
                </div>
                <div class="ad-dates">
                  <el-date-picker
                    v-model="ad.dateRange"
                    type="datetimerange"
                    range-separator="-"
                    :start-placeholder="$t('searchOrder.start')"
                    :end-placeholder="$t('searchOrder.end')"
                    value-format="yyyy-MM-dd HH:mm:ss"
                    format="yyyy-MM-dd HH:mm:ss"
                    :default-time="['00:00:00', '23:59:59']"
                    class="config-date-picker config-date-range-picker"
                    clearable
                  />
                </div>
                <button class="btn btn-delete-ad" @click="deleteAd('screen', index)"><i class="el-icon-delete"></i></button>
              </div>
              <p class="queue-info">{{ screenAds.length }} screen ads in queue. Drag items to reorder playback sequence.</p>
              <div v-if="isAdmin" class="network-statistics">
                <h3 class="stats-title">Network Statistics</h3>
                <div class="stats-container">
                  <div class="stat-item">
                    <p class="stat-label">Total Impressions</p>
                    <p class="stat-value vtm-num">{{ totalImpressions.toLocaleString() }}</p>
                  </div>
                  <div class="stat-item">
                    <p class="stat-label">Paid Ad Revenue</p>
                    <p class="stat-value revenue vtm-num">${{ paidAdRevenue.toFixed(2) }}</p>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div v-if="activeTab === 'banner'" class="ads-list">
            <div v-if="bannerAds.length === 0" class="empty-state">
              <p>No Banner ads in queue</p>
              <a href="#" class="add-link" @click.prevent="openSelectAdsModal">Add from gallery</a>
            </div>
            <template v-else>
              
              <div 
                v-for="(ad, index) in bannerAds" 
                :key="ad.id"
                class="ad-item"
                draggable="true"
                @dragstart="dragStart($event, 'banner', index)"
                @dragover="dragOver($event)"
                @drop="drop($event, 'banner', index)"
                @dragend="dragEnd"
              >
                <div class="ad-number">#{{ index + 1 }}</div>
                <div class="drag-handle">
                  <div class="dot-row">• •</div>
                  <div class="dot-row">• •</div>
                  <div class="dot-row">• •</div>
                </div>
                <img
                  v-if="ad.mediaType !== 'video'"
                  :src="ad.image"
                  :alt="ad.name"
                  class="ad-image"
                  @error="handleAdImageError"
                />
                <video
                  v-else
                  :src="ad.image"
                  class="ad-image ad-image-video"
                  muted
                  playsinline
                  autoplay
                  loop
                />
                <div class="ad-info">
                  <h4 class="ad-name">{{ ad.name }}</h4>
                  <p class="ad-brand">{{ ad.brand }}</p>
                </div>
                <div class="ad-dates">
                  <el-date-picker
                    v-model="ad.dateRange"
                    type="datetimerange"
                    range-separator="-"
                    :start-placeholder="$t('searchOrder.start')"
                    :end-placeholder="$t('searchOrder.end')"
                    value-format="yyyy-MM-dd HH:mm:ss"
                    format="yyyy-MM-dd HH:mm:ss"
                    :default-time="['00:00:00', '23:59:59']"
                    class="config-date-picker config-date-range-picker"
                    clearable
                  />
                </div>
                <button class="btn btn-delete-ad" @click="deleteAd('banner', index)"><i class="el-icon-delete"></i></button>
              </div>
              <p class="queue-info">{{ bannerAds.length }} banner ads in queue. Drag items to reorder playback sequence.</p>
              <div v-if="isAdmin" class="network-statistics">
                <h3 class="stats-title">Network Statistics</h3>
                <div class="stats-container">
                  <div class="stat-item">
                    <p class="stat-label">Total Impressions</p>
                    <p class="stat-value vtm-num">{{ totalImpressions.toLocaleString() }}</p>
                  </div>
                  <div class="stat-item">
                    <p class="stat-label">Paid Ad Revenue</p>
                    <p class="stat-value revenue vtm-num">${{ paidAdRevenue.toFixed(2) }}</p>
                  </div>
                </div>
              </div>
            </template>

          </div>
          </template>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeAdsConfig">Cancel</button>
          <button class="btn btn-save" :disabled="configLoading" @click="saveAdsConfig">Save Configuration</button>
        </div>
      </div>
    </div>

    <!-- Create Ad Group Modal -->
    <div v-if="showCreateModal" class="modal-overlay modal-overlay-centered" @click.self="closeCreateModal">
      <div class="modal-content create-modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <h2 class="modal-title">{{ selectedGroup.id ? 'Edit Ad Group' : 'Create Ad Group' }}</h2>
          <button class="close-btn" @click="closeCreateModal">×</button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body create-body">
          <!-- Group Name -->
          <div class="form-group">
            <label class="form-label">Group Name</label>
            <input 
              v-model="newGroupForm.groupName"
              type="text" 
              class="form-input"
              placeholder="Enter group name"
            />
          </div>

          <!-- Notes -->
          <div class="form-group">
            <label class="form-label">Notes</label>
            <textarea 
              v-model="newGroupForm.notes"
              class="form-textarea"
              placeholder="Optional notes..."
              rows="4"
            ></textarea>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeCreateModal">Cancel</button>
          <button class="btn btn-save" @click="selectedGroup.id ? updateAdGroup() : createAdGroup()">
            {{ selectedGroup.id ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Ad Group Modal -->
    <div v-if="showAssignModal" class="modal-overlay modal-overlay-centered" @click.self="closeAssignModal">
      <div class="modal-content assign-modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <h2 class="modal-title">Edit Ad Group</h2>
          <button class="close-btn" @click="closeAssignModal">×</button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body assign-body">
          <!-- Group Name -->
          <div class="form-group">
            <label class="form-label">Group Name</label>
            <input 
              type="text" 
              v-model="selectedGroup.groupName" 
              class="form-input"
              readonly
            />
          </div>

          <!-- Notes (toggle to show/hide) -->
          <div class="form-group notes-toggle-group">
            <button 
              type="button" 
              class="notes-toggle-link"
              @click="showNotesInEditGroup = !showNotesInEditGroup"
            >
              {{ showNotesInEditGroup ? 'Hide Notes' : 'Edit Notes' }}
            </button>
            <template v-if="showNotesInEditGroup">
              <label class="form-label">Notes</label>
              <textarea 
                v-model="selectedGroup.notes" 
                class="form-textarea"
                rows="4"
              ></textarea>
            </template>
          </div>

          <!-- Assign Machines Section -->
          <div class="assign-machines-section">
            <div class="assign-header">
              <h3 class="assign-title">Assign Machines</h3>
              <span class="machines-count">{{ assignedMachinesCount }} machines assigned</span>
            </div>

            <!-- Tabs -->
            <div class="assign-tabs">
              <button 
                class="assign-tab" 
                :class="{ active: assignTab === 'individual' }"
                @click="assignTab = 'individual'"
              >
                <i class="el-icon-setting"></i> Individual Machines
              </button>
              <button 
                class="assign-tab" 
                :class="{ active: assignTab === 'groups' }"
                @click="assignTab = 'groups'"
              >
                <i class="el-icon-folder"></i> Machine Groups
              </button>
            </div>

            <!-- Tab Content -->
            <div class="assign-content">
              <!-- Individual Machines Tab -->
              <div v-if="assignTab === 'individual'" class="machines-list">
                <div class="search-box">
                  <input 
                    type="text" 
                    placeholder="Search machines..." 
                    v-model="machineSearchQuery"
                    class="search-input-modal"
                  />
                </div>
                <div class="machines-items">
                  <div v-if="assignMachinesLoading" class="assign-loading">Loading machines...</div>
                  <template v-else>
                    <div 
                      v-for="machine in filteredIndividualMachines" 
                      :key="machine.id"
                      class="machine-item"
                    >
                      <input 
                        type="checkbox" 
                        :id="'machine-' + machine.id"
                        v-model="machine.selected"
                        class="machine-checkbox"
                      />
                      <label :for="'machine-' + machine.id" class="machine-label">
                        <div class="machine-info">
                          <span class="machine-id">{{ machine.equipmentName ? machine.equipmentName + ' (' + (machine.equipmentNo || machine.id || '') + ')' : (machine.equipmentNo || machine.id || 'N/A') }}</span>
                          <span class="machine-address">{{ machine.address }}</span>
                        </div>
                        <span class="machine-group">{{ machine.group }}</span>
                      </label>
                    </div>
                    <div v-if="!assignMachinesLoading && filteredIndividualMachines.length === 0" class="assign-empty">No machines found</div>
                  </template>
                </div>
              </div>

              <!-- Machine Groups Tab -->
              <div v-if="assignTab === 'groups'" class="groups-list">
                <div class="search-box">
                  <input 
                    type="text" 
                    placeholder="Search groups..." 
                    v-model="groupSearchQuery"
                    class="search-input-modal"
                  />
                </div>
                <div class="groups-items">
                  <div v-if="assignGroupsLoading" class="assign-loading">Loading groups...</div>
                  <template v-else>
                    <div 
                      v-for="group in filteredMachineGroups" 
                      :key="group.id"
                      class="group-item"
                    >
                      <input
                        type="checkbox"
                        :id="'group-' + group.id"
                        v-model="group.selected"
                        @change="onGroupCheckboxChange(group)"
                        class="group-checkbox"
                      />
                      <label :for="'group-' + group.id" class="group-label">
                        <div class="group-info">
                          <span class="group-name-text">{{ group.name }}</span>
                          <span class="group-date">Created {{ group.createdDate }}</span>
                        </div>
                        <span class="group-count">{{ group.machineCount }} machines</span>
                      </label>
                    </div>
                    <div v-if="!assignGroupsLoading && filteredMachineGroups.length === 0" class="assign-empty">No groups found</div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeAssignModal">Cancel</button>
          <button class="btn btn-save" @click="saveAssignChanges">Save Changes</button>
        </div>
      </div>
    </div>

    <!-- Select Ads Modal -->
    <div v-if="showSelectAdsModal" class="modal-overlay modal-overlay-centered" @click.self="closeSelectAdsModal">
      <div class="modal-content select-ads-modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <h2 class="modal-title">Select Ads for <span class="modal-badge">{{ activeTab === 'screen' ? '📺 Screen' : '📸 Banner' }}</span> Queue</h2>
          <button class="close-btn" @click="closeSelectAdsModal">×</button>
        </div>

        <!-- Search Box -->
        <div class="modal-search-section">
          <input 
            type="text" 
            v-model="adsSearchQuery"
            placeholder="Search ads by title or company..." 
            class="ads-search-input"
          />
        </div>

        <!-- Modal Body - Ads Grid -->
        <div class="modal-body select-ads-body">
          <div v-if="adsListLoading" class="ads-loading-state">Loading ads...</div>
          <template v-else>
            <div class="ads-grid">
              <div 
                v-for="ad in filteredAvailableAds"
                :key="ad.id"
                class="ad-card"
                :class="{ selected: ad.selected }"
              >
                <div class="ad-card-header">
                  <input 
                    type="checkbox"
                    v-model="ad.selected"
                    class="ad-card-checkbox"
                  />
                </div>
                <img
                  v-if="ad.mediaType !== 'video'"
                  :src="ad.image"
                  :alt="ad.name"
                  class="ad-card-image"
                  @error="handleAdImageError"
                />
                <video
                  v-else
                  :src="ad.image"
                  class="ad-card-image ad-card-video"
                  muted
                  playsinline
                  autoplay
                  loop
                />
                <div class="ad-card-content">
                  <h4 class="ad-card-title">{{ ad.name }}</h4>
                  <p class="ad-card-brand">{{ ad.brand }}</p>
                </div>
              </div>
            </div>
            <div v-if="filteredAvailableAds.length === 0" class="empty-ads-state">
              <p>No ads found</p>
            </div>
          </template>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeSelectAdsModal">Cancel</button>
          <button class="btn btn-save" @click="addSelectedAds" :disabled="selectedAdsCount === 0">
            Add ({{ selectedAdsCount }}) Ads
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '@/interface/index';
import url from '@/interface/url';
import methods from '@/interface/method';

export default {
  name: 'AdvertGroup',
  data() {
    return {
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      goToPage: '',
      showAdsModal: false,
      activeTab: 'screen',
      selectedGroup: {},
      draggedIndex: null,
      draggedType: null,
      isAdmin: false,
      totalImpressions: 12847,
      paidAdRevenue: 1247.50,
      
      // Select Ads Modal (populated from API by display: 1 = Screen, 2 = Banner)
      showSelectAdsModal: false,
      adsSearchQuery: '',
      availableAds: [],
      screenAdsAvailable: [],
      bannerAdsAvailable: [],
      adsListLoading: false,
      configLoading: false,
      // Create Modal refs
      showCreateModal: false,
      newGroupForm: {
        groupName: '',
        notes: ''
      },
      
      // Assign Modal refs
      showAssignModal: false,
      showNotesInEditGroup: false,
      assignTab: 'individual',
      machineSearchQuery: '',
      groupSearchQuery: '',
      
      // Screen Ads sample data
      screenAds: [
        {
          id: 1,
          name: 'Summer Sale Campaign',
          brand: 'Coca-Cola',
          image: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%2280%22%3E%3Crect fill=%22%23d32f2f%22 width=%22100%22 height=%2280%22/%3E%3Ctext x=%2250%22 y=%2240%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23ffffff%22 font-size=%2220%22%3E🥤%3C/text%3E%3C/svg%3E',
          startDate: '',
          endDate: '',
          dateRange: null
        },
        {
          id: 2,
          name: 'Winter Promotion',
          brand: 'Pepsi',
          image: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%2280%22%3E%3Crect fill=%22%231976d2%22 width=%22100%22 height=%2280%22/%3E%3Ctext x=%2250%22 y=%2240%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23ffffff%22 font-size=%2220%22%3E❄️%3C/text%3E%3C/svg%3E',
          startDate: '',
          endDate: '',
          dateRange: null
        },
        {
          id: 3,
          name: 'Spring Launch',
          brand: 'Sprite',
          image: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%2280%22%3E%3Crect fill=%22%2376ff03%22 width=%22100%22 height=%2280%22/%3E%3Ctext x=%2250%22 y=%2240%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23000000%22 font-size=%2220%22%3E🌿%3C/text%3E%3C/svg%3E',
          startDate: '',
          endDate: '',
          dateRange: null
        }
      ],
      
      // Banner Ads sample data
      bannerAds: [
        {
          id: 1,
          name: 'New Energy Drink',
          brand: 'RedBull',
          image: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%2280%22%3E%3Crect fill=%22%23222222%22 width=%22100%22 height=%2280%22/%3E%3Ctext x=%2250%22 y=%2240%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23ffeb3b%22 font-size=%2220%22%3ERB%3C/text%3E%3C/svg%3E',
          startDate: '',
          endDate: '',
          dateRange: null
        },
        {
          id: 2,
          name: 'Healthy Snacks',
          brand: 'Nature Valley',
          image: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%2280%22%3E%3Crect fill=%22%23ff9800%22 width=%22100%22 height=%2280%22/%3E%3Ctext x=%2250%22 y=%2240%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23ffffff%22 font-size=%2218%22%3E🥗%3C/text%3E%3C/svg%3E',
          startDate: '',
          endDate: '',
          dateRange: null
        },
        {
          id: 3,
          name: 'Refreshing Water',
          brand: 'Evian',
          image: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%2280%22%3E%3Crect fill=%22%230288d1%22 width=%22100%22 height=%2280%22/%3E%3Ctext x=%2250%22 y=%2240%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23ffffff%22 font-size=%2220%22%3E💧%3C/text%3E%3C/svg%3E',
          startDate: '',
          endDate: '',
          dateRange: null
        },
        {
          id: 4,
          name: 'Sports Drink',
          brand: 'Gatorade',
          image: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%2280%22%3E%3Crect fill=%22%23c62828%22 width=%22100%22 height=%2280%22/%3E%3Ctext x=%2250%22 y=%2240%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23ffffff%22 font-size=%2220%22%3E⚡%3C/text%3E%3C/svg%3E',
          startDate: '',
          endDate: '',
          dateRange: null
        }
      ],
      
      // Individual Machines (loaded from API when Assign modal opens)
      individualMachines: [],

      // Machine Groups (loaded from API when Assign modal opens)
      machineGroups: [],

      // groupId -> member keys ("no:<machine number>" or "id:<equipment id>") for currently-checked
      // groups, never raw ids to send to the backend. Lets the assigned count and the
      // save payload can de-dupe a machine that's both individually checked AND a member
      // of a checked group, instead of counting/sending it twice.
      groupEquipmentCache: {},

      assignMachinesLoading: false,
      assignGroupsLoading: false,
      
      // Ad groups data - will be populated from API
      allData: []
    };
  },
  mounted() {
    this.fetchAdGroups();
  },
  computed: {
    filteredData() {
      if (!this.searchQuery) {
        return this.allData;
      }
      return this.allData.filter(item =>
        item.groupName.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        item.userAccount.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        item.notes.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    totalItems() {
      return this.filteredData.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredData.slice(start, end);
    },
    filteredIndividualMachines() {
      if (!this.machineSearchQuery) {
        return this.individualMachines;
      }
      const q = this.machineSearchQuery.toLowerCase();
      return this.individualMachines.filter(machine =>
        (machine.equipmentNo || machine.id || '').toString().toLowerCase().includes(q) ||
        (machine.equipmentName || '').toLowerCase().includes(q) ||
        (machine.address || '').toLowerCase().includes(q) ||
        (machine.group || '').toLowerCase().includes(q)
      );
    },
    filteredMachineGroups() {
      if (!this.groupSearchQuery) {
        return this.machineGroups;
      }
      return this.machineGroups.filter(group =>
        group.name.toLowerCase().includes(this.groupSearchQuery.toLowerCase())
      );
    },
    // Machines covered by currently-checked groups (not just a count), so overlap with
    // individually-checked machines can be detected rather than double-counted. Entries are
    // "no:<machine number>", or "id:<equipment id>" from a backend that doesn't send numbers yet.
    checkedGroupEquipmentIds() {
      const ids = new Set();
      this.machineGroups.filter(g => g.selected).forEach(g => {
        const cached = this.groupEquipmentCache[g.id];
        if (cached) {
          cached.forEach(id => ids.add(String(id)));
        }
      });
      return ids;
    },
    assignedMachinesCount() {
      const covered = this.checkedGroupEquipmentIds;
      // A just-checked group's real member IDs load asynchronously; until that resolves,
      // fall back to its known machineCount so the total doesn't dip to 0 for that group.
      let pendingGroupEstimate = 0;
      this.machineGroups.filter(g => g.selected).forEach(g => {
        if (!this.groupEquipmentCache[g.id]) {
          pendingGroupEstimate += g.machineCount || 0;
        }
      });
      const individualOnly = this.individualMachines.filter(
        m => m.selected && !this.isCoveredByGroup(m, covered)
      ).length;
      return covered.size + pendingGroupEstimate + individualOnly;
    },
    filteredAvailableAds() {
      if (!this.adsSearchQuery) {
        return this.availableAds;
      }
      const q = this.adsSearchQuery.toLowerCase();
      return this.availableAds.filter(ad =>
        (ad.name || '').toLowerCase().includes(q) ||
        (ad.brand || '').toLowerCase().includes(q)
      );
    },
    selectedAdsCount() {
      return this.availableAds.filter(ad => ad.selected).length;
    }
  },
  methods: {
    /**
     * Fetch ad groups from the backend using adTeam/page API
     */
    fetchAdGroups() {
      console.log('🚀 fetchAdGroups called - Starting API request');
      const params = {
        pageIndex: this.currentPage,
        pageSize: 100  // Fetch more items at once
      };

      console.log('📡 Calling API with params:', params);
      console.log('📡 API URL:', url.AdvertGroup.page);
      
      request.request(url.AdvertGroup.page, methods.get, params)
        .then(response => {
          console.log('📥 API Response received:', response);
          if (response.code === 0) {
            const data = response.data || {};
            const groupsList = data.records || data.list || [];
            
            console.log('📊 Groups list from API:', groupsList);
            
            // Transform API response to match table structure
            this.allData = groupsList.map(item => ({
              id: item.id,
              uId: item.uId,
              userAccount: item.userName || item.userAccount || 'N/A',
              groupName: item.name || item.groupName || 'N/A',
              notes: item.notes || item.description || 'N/A',
              creationTime: item.createTime || item.creationTime || new Date().toLocaleString()
            }));
            
            console.log('✅ Ad groups fetched successfully:', this.allData);
          } else {
            this.$message.error(response.msg || 'Failed to load ad groups');
          }
        })
        .catch(error => {
          console.error('❌ Error fetching ad groups:', error);
          console.error('❌ Error details:', error.message, error.response);
          this.$message.error('Error loading ad groups');
        });
    },
    handleSearch() {
      console.log('🔍 handleSearch called');
      this.currentPage = 1;
    },
    handleReset() {
      console.log('🔄 handleReset called');
      this.searchQuery = '';
      this.currentPage = 1;
    },
    openCreateModal() {
      console.log('📝 openCreateModal called');
      this.showCreateModal = true;
      this.newGroupForm = { groupName: '', notes: '' };
    },
    closeCreateModal() {
      console.log('📝 closeCreateModal called');
      this.showCreateModal = false;
      this.newGroupForm = { groupName: '', notes: '' };
      this.selectedGroup = {}; // Reset selected group
    },
    createAdGroup() {
      console.log('✅ createAdGroup called with:', this.newGroupForm);
      if (!this.newGroupForm.groupName.trim()) {
        this.$message.error('Please enter a group name');
        return;
      }
      
      const data = {
        name: this.newGroupForm.groupName,
        notes: this.newGroupForm.notes
      };
      
      request.request(url.AdvertGroup.insert, methods.post, data)
        .then(response => {
          console.log('✅ Create response:', response);
          if (response.code === 0) {
            this.$message.success('Ad group created successfully');
            this.closeCreateModal();
            this.fetchAdGroups(); // Refresh the list
          } else {
            this.$message.error(response.msg || 'Failed to create ad group');
          }
        })
        .catch(error => {
          console.error('❌ Error creating ad group:', error);
          this.$message.error('Error creating ad group');
        });
    },
    deleteAdGroup(item) {
      console.log('🗑️ deleteAdGroup called with:', item);
      this.$confirm('Are you sure you want to delete this ad group?', 'Warning', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        const data = { id: item.id };
        request.request(url.AdvertGroup.delete, methods.delete, data)
          .then(response => {
            console.log('✅ Delete response:', response);
            if (response.code === 0) {
              this.$message.success('Ad group deleted successfully');
              this.fetchAdGroups(); // Refresh the list
            } else {
              this.$message.error(response.msg || 'Failed to delete ad group');
            }
          })
          .catch(error => {
            console.error('❌ Error deleting ad group:', error);
            this.$message.error('Error deleting ad group');
          });
      }).catch(() => {
        console.log('Delete cancelled');
      });
    },
    openEditAdGroup(item) {
      console.log('✏️ openEditAdGroup called with:', item);
      this.selectedGroup = { ...item };
      this.newGroupForm = {
        groupName: item.groupName,
        notes: item.notes
      };
      this.showCreateModal = true; // Reuse create modal for edit
    },
    updateAdGroup() {
      console.log('✅ updateAdGroup called with:', this.newGroupForm);
      if (!this.newGroupForm.groupName.trim()) {
        this.$message.error('Please enter a group name');
        return;
      }
      
      const data = {
        id: this.selectedGroup.id,
        name: this.newGroupForm.groupName,
        notes: this.newGroupForm.notes
      };
      
      request.request(url.AdvertGroup.update, methods.put, data)
        .then(response => {
          console.log('✅ Update response:', response);
          if (response.code === 0) {
            this.$message.success('Ad group updated successfully');
            this.closeCreateModal();
            this.fetchAdGroups(); // Refresh the list
          } else {
            this.$message.error(response.msg || 'Failed to update ad group');
          }
        })
        .catch(error => {
          console.error('❌ Error updating ad group:', error);
          this.$message.error('Error updating ad group');
        });
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    handleGoTo() {
      const page = parseInt(this.goToPage);
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.goToPage = '';
      }
    },
    openAdsConfig(item) {
      this.selectedGroup = item;
      this.showAdsModal = true;
      this.screenAds = [];
      this.bannerAds = [];
      this.fetchAdsList();
      this.fetchAdTeamConfig(item.id);
    },
    closeAdsConfig() {
      this.showAdsModal = false;
      this.selectedGroup = {};
      this.activeTab = 'screen';
    },
    /**
     * Save ad team config (POST save-config). Sends teamId and ads (adId, sort, startTime, endTime).
     */
    saveAdsConfig() {
      const teamId = this.selectedGroup && this.selectedGroup.id;
      if (!teamId) {
        this.$message.error('No ad team selected');
        return;
      }
      const allAds = [...this.screenAds, ...this.bannerAds];
      for (let i = 0; i < allAds.length; i++) {
        const ad = allAds[i];
        const startVal = (ad.dateRange && ad.dateRange[0]) ? ad.dateRange[0] : ad.startDate;
        const endVal = (ad.dateRange && ad.dateRange[1]) ? ad.dateRange[1] : ad.endDate;
        const hasStart = startVal && String(startVal).trim() !== '';
        const hasEnd = endVal && String(endVal).trim() !== '';
        if (!hasStart) {
          this.$message.error(`Please select Start date for "${ad.name || 'Ad'}"`);
          return;
        }
        if (!hasEnd) {
          this.$message.error(`Please select End date for "${ad.name || 'Ad'}"`);
          return;
        }
        const startMs = new Date(startVal).getTime();
        const endMs = new Date(endVal).getTime();
        if (endMs < startMs) {
          this.$message.error(`End date must be on or after Start date for "${ad.name || 'Ad'}"`);
          return;
        }
      }
      const ads = [];
      let sort = 1;
      allAds.forEach(ad => {
        const startTime = (ad.dateRange && ad.dateRange[0]) ? ad.dateRange[0] : ad.startDate;
        const endTime = (ad.dateRange && ad.dateRange[1]) ? ad.dateRange[1] : ad.endDate;
        ads.push({
          adId: String(ad.id),
          sort: sort++,
          startTime: startTime || null,
          endTime: endTime || null
        });
      });
      this.configLoading = true;
      request.request(url.AdTeamConfig.saveConfig, methods.post, { teamId: String(teamId), ads })
        .then(res => {
          if (res.code === 0) {
            this.$message.success('Configuration saved');
            this.closeAdsConfig();
            this.fetchAdGroups();
          } else {
            this.$message.error(res.msg || 'Failed to save configuration');
          }
        })
        .catch(err => {
          console.error('Error saving ad team config:', err);
          this.$message.error('Error saving configuration');
        })
        .finally(() => {
          this.configLoading = false;
        });
    },
    inferGalleryMediaType(mediaUrl) {
      if (!mediaUrl || typeof mediaUrl !== 'string') return 'image';
      const path = mediaUrl.split('?')[0];
      const ext = (path.split('.').pop() || '').toLowerCase();
      if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) return 'video';
      return 'image';
    },
    /**
     * Fetch ads from ad/list (same API as Adverts.vue). Splits by display: 1 = Screen, 2 = Banner.
     */
    fetchAdsList() {
      this.adsListLoading = true;
      request.request(url.Adverts.listAdvertByGroupId, methods.get, {})
        .then(res => {
          if (res.code === 0 || res.code === 200) {
            const list = res.data || [];
            const mapAd = (ad) => {
              const mediaUrl = ad.thum || ad.url || '';
              return {
                id: ad.adId != null ? ad.adId : ad.id,
                name: ad.head || '',
                brand: ad.advertiser || '',
                image: mediaUrl,
                mediaType: this.inferGalleryMediaType(mediaUrl),
                selected: false
              };
            };
            // display: 1 = Screen, 2 = Banner; fallback type: 1 = Screen, 0 = Banner (same as Adverts.vue)
            const isScreen = (ad) => ad.display === 1 || (ad.display == null && ad.type === 1);
            const isBanner = (ad) => ad.display === 2 || (ad.display == null && ad.type === 0);
            this.screenAdsAvailable = list.filter(isScreen).map(mapAd);
            this.bannerAdsAvailable = list.filter(isBanner).map(mapAd);
          } else {
            this.$message.error(res.msg || 'Failed to load ads');
          }
        })
        .catch(err => {
          console.error('Error fetching ads:', err);
          this.$message.error('Error loading ads');
        })
        .finally(() => {
          this.adsListLoading = false;
        });
    },
    /**
     * Fetch ad team config (GET config/{teamId}) and populate Screen/Banner ads.
     */
    fetchAdTeamConfig(teamId) {
      if (!teamId) return;
      this.configLoading = true;
      const configUrl = `${url.AdTeamConfig.getConfig}/${teamId}`;
      request.request(configUrl, methods.get, {})
        .then(res => {
          if (res.code === 0 && Array.isArray(res.data)) {
            const mapItem = (item) => {
              const startTime = item.startTime ? String(item.startTime).trim() : '';
              const endTime = item.endTime ? String(item.endTime).trim() : '';
              const startFormatted = startTime ? (startTime.length >= 19 ? startTime : startTime + ' 00:00:00') : '';
              const endFormatted = endTime ? (endTime.length >= 19 ? endTime : endTime + ' 23:59:59') : '';
              const mediaUrl = item.thum || item.url || '';
              return {
                id: item.adId,
                name: item.head || '',
                brand: item.advertiser || '',
                image: mediaUrl,
                mediaType: this.inferGalleryMediaType(mediaUrl),
                startDate: startFormatted,
                endDate: endFormatted,
                dateRange: startFormatted && endFormatted ? [startFormatted, endFormatted] : null
              };
            };
            const isScreen = (item) => item.display === 1 || (item.display == null && item.type === 1);
            const isBanner = (item) => item.display === 2 || (item.display == null && item.type === 0);
            const bySort = (a, b) => (a.sort != null ? a.sort : 0) - (b.sort != null ? b.sort : 0);
            this.screenAds = res.data.filter(isScreen).sort(bySort).map(mapItem);
            this.bannerAds = res.data.filter(isBanner).sort(bySort).map(mapItem);
          } else {
            this.screenAds = [];
            this.bannerAds = [];
          }
        })
        .catch(err => {
          console.error('Error fetching ad team config:', err);
          this.screenAds = [];
          this.bannerAds = [];
          this.$message.error('Error loading ad team config');
        })
        .finally(() => {
          this.configLoading = false;
        });
    },
    dragStart(event, type, index) {
      this.draggedIndex = index;
      this.draggedType = type;
      event.dataTransfer.effectAllowed = 'move';
    },
    dragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },
    drop(event, type, index) {
      event.preventDefault();
      if (this.draggedIndex !== null && this.draggedIndex !== index && this.draggedType === type) {
        const targetArray = type === 'screen' ? this.screenAds : this.bannerAds;
        const draggedItem = targetArray[this.draggedIndex];
        targetArray.splice(this.draggedIndex, 1);
        targetArray.splice(index, 0, draggedItem);
        this.draggedIndex = null;
        this.draggedType = null;
      }
    },
    dragEnd() {
      this.draggedIndex = null;
      this.draggedType = null;
    },
    deleteAd(type, index) {
      const targetArray = type === 'screen' ? this.screenAds : this.bannerAds;
      targetArray.splice(index, 1);
    },
    openAssignModal(item) {
      this.selectedGroup = { ...item };
      this.showAssignModal = true;
      this.showNotesInEditGroup = false;
      this.assignTab = 'individual';
      this.machineSearchQuery = '';
      this.groupSearchQuery = '';
      this.groupEquipmentCache = {};
      this.fetchMachineGroups();
      this.fetchIndividualMachines();
    },
    closeAssignModal() {
      this.showAssignModal = false;
      this.showNotesInEditGroup = false;
      this.selectedGroup = {};
      this.assignTab = 'individual';
      this.machineSearchQuery = '';
      this.groupSearchQuery = '';
      this.individualMachines.forEach(m => m.selected = false);
      this.machineGroups.forEach(g => g.selected = false);
      this.groupEquipmentCache = {};
    },
    isCoveredByGroup(machine, covered) {
      return covered.has('no:' + machine.equipmentNo) || covered.has('id:' + machine.id);
    },
    /**
     * Loads (and caches) a group's members the first time it's checked, so
     * assignedMachinesCount and saveAssignChanges can tell when an individually-checked
     * machine is already covered by a checked group instead of counting/sending it twice.
     */
    onGroupCheckboxChange(group) {
      if (group.selected && !this.groupEquipmentCache[group.id]) {
        request.request(url.UserMachineGroup.getMachineGroup, methods.post, [String(group.id)])
          .then(response => {
            const detail = response.code === 0 && Array.isArray(response.data) ? response.data[0] : null;
            // eqIds are equipment ids but the individual list holds user_equipment ids, so they
            // never match; match on the machine number (eqNos lines up with eqIds) where there is one
            const ids = detail && Array.isArray(detail.eqIds) ? detail.eqIds : [];
            const nos = detail && Array.isArray(detail.eqNos) ? detail.eqNos : [];
            const members = ids.map((id, i) => (nos[i] ? 'no:' + nos[i] : 'id:' + id));
            this.$set(this.groupEquipmentCache, group.id, members);
          })
          .catch(error => {
            console.error('Error fetching group machine ids:', error);
            this.$set(this.groupEquipmentCache, group.id, []);
          });
      }
    },
    /**
     * Fetch machine groups from userEquipment/fetchGroupMachines
     */
    fetchMachineGroups() {
      this.assignGroupsLoading = true;
      const params = {};
      if (this.selectedGroup && this.selectedGroup.uId) {
        params.uId = this.selectedGroup.uId;
      } else if (this.selectedGroup && this.selectedGroup.userAccount && this.selectedGroup.userAccount !== 'N/A') {
        params.userName = this.selectedGroup.userAccount;
      }
      request.request(url.UserMachineGroup.fetchGroupMachines, methods.get, params)
        .then(response => {
          if (response.code === 0) {
            const groups = response.data || [];
            this.machineGroups = groups.map(item => ({
              id: item.groupId,
              name: item.groupName,
              createdDate: item.createdDate ? String(item.createdDate).slice(0, 10) : '',
              machineCount: item.machineCount != null ? item.machineCount : 0,
              selected: false
            }));
          } else {
            this.$message.error(response.msg || 'Failed to load machine groups');
          }
        })
        .catch(error => {
          console.error('Error fetching machine groups:', error);
          this.$message.error('Error fetching machine groups');
        })
        .finally(() => {
          this.assignGroupsLoading = false;
        });
    },
    /**
     * Fetch individual machines from userEquipment/fetchMachineDetails (same as PushPaidAds – reduces load time)
     */
    fetchIndividualMachines() {
      this.assignMachinesLoading = true;
      const uId = this.selectedGroup && this.selectedGroup.uId;
      const userAccount = this.selectedGroup && this.selectedGroup.userAccount;
      let endpoint, params;
      if (uId) {
        endpoint = url.UserMachine.fetchMachineDetails;
        params = { uId };
      } else if (userAccount && userAccount !== 'N/A') {
        // Fallback: filter by username via select endpoint
        endpoint = url.UserMachine.listUserMachineSelect;
        params = { userName: userAccount, pageSize: 10000, pageIndex: 1 };
      } else {
        endpoint = url.UserMachine.fetchMachineDetails;
        params = {};
      }
      request.request(endpoint, methods.get, params)
        .then(res => {
          if (res.code === 0 && res.data) {
            const list = Array.isArray(res.data) ? res.data : (res.data.records || []);
            this.individualMachines = list.map(item => this.mapSelectRecordToMachine(item));
          } else {
            this.$message.error(res.msg || 'Failed to load machines');
          }
        })
        .catch(error => {
          console.error('Error fetching individual machines:', error);
          this.$message.error('Error fetching individual machines');
        })
        .finally(() => {
          this.assignMachinesLoading = false;
        });
    },
    /**
     * Map API record to unified machine shape (same as PushPaidAds).
     * Supports fetchMachineDetails: { equipmentId, equipmentNum, equipmentName, locale }
     * and userEquipment/select: { id, equipmentNo, name, locale, teamName }.
     */
    mapSelectRecordToMachine(record) {
      const id = record.equipmentId != null ? record.equipmentId : record.id;
      const equipmentNo = record.equipmentNum != null ? record.equipmentNum : record.equipmentNo;
      const equipmentName = (record.equipmentName != null ? record.equipmentName : record.name) || equipmentNo || '';
      const address = record.locale != null && record.locale !== '' ? record.locale : '';
      const group = record.teamName != null ? record.teamName : '';
      return {
        id,
        equipmentNo: equipmentNo != null ? equipmentNo : '',
        equipmentName: equipmentName != null ? equipmentName : '',
        address,
        group,
        selected: false
      };
    },
    saveAssignChanges() {
      const teamId = this.selectedGroup && this.selectedGroup.id;
      if (!teamId) {
        this.$message.error('No ad team selected');
        this.closeAssignModal();
        return;
      }
      const targets = [];
      this.machineGroups.filter(g => g.selected).forEach(g => {
        targets.push({ target: 1, groupId: String(g.id) });
      });
      // Skip individually-checked machines already covered by a checked group: sending both
      // a group and an individual target for the same machine silently detaches it from the
      // group (the backend's last-processed target wins) even though both looked checked here.
      const covered = this.checkedGroupEquipmentIds;
      this.individualMachines.filter(m => m.selected && !this.isCoveredByGroup(m, covered)).forEach(m => {
        targets.push({ target: 2, eqId: String(m.id) });
      });
      if (targets.length === 0) {
        this.$message.warning('Select at least one machine or group');
        return;
      }
      const payload = {
        teamId: String(teamId),
        name: this.selectedGroup.groupName != null ? String(this.selectedGroup.groupName) : '',
        notes: this.selectedGroup.notes != null ? String(this.selectedGroup.notes) : '',
        targets
      };
      request.request(url.AdTeamConfig.assign, methods.post, payload)
        .then(res => {
          if (res.code === 0) {
            this.$message.success('Assignment saved');
            this.closeAssignModal();
            this.fetchAdGroups();
          } else {
            this.$message.error(res.msg || 'Failed to save assignment');
          }
        })
        .catch(err => {
          console.error('Error assigning ad team:', err);
          this.$message.error('Error saving assignment');
        });
    },
    openSelectAdsModal() {
      this.adsSearchQuery = '';
      const source = this.activeTab === 'screen' ? this.screenAdsAvailable : this.bannerAdsAvailable;
      this.availableAds = source.map(ad => ({
        ...ad,
        selected: false
      }));
      this.showSelectAdsModal = true;
    },
    closeSelectAdsModal() {
      this.showSelectAdsModal = false;
      this.adsSearchQuery = '';
      this.availableAds.forEach(ad => { ad.selected = false; });
    },
    addSelectedAds() {
      const selectedAds = this.availableAds.filter(ad => ad.selected);
      
      if (selectedAds.length === 0) {
        this.$message.warning('Please select at least one ad');
        return;
      }
      
      if (this.activeTab === 'screen') {
        selectedAds.forEach(ad => {
          this.screenAds.push({
            id: ad.id,
            name: ad.name,
            brand: ad.brand,
            image: ad.image,
            mediaType: ad.mediaType || this.inferGalleryMediaType(ad.image),
            startDate: '',
            endDate: '',
            dateRange: null
          });
        });
      } else {
        selectedAds.forEach(ad => {
          this.bannerAds.push({
            id: ad.id,
            name: ad.name,
            brand: ad.brand,
            image: ad.image,
            mediaType: ad.mediaType || this.inferGalleryMediaType(ad.image),
            startDate: '',
            endDate: '',
            dateRange: null
          });
        });
      }
      
      this.closeSelectAdsModal();
    },
    handleAdImageError(event) {
      if (event && event.target) {
        try {
          event.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect fill="#f0f0f0" width="80" height="80"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#999" font-size="10" font-family="sans-serif">No image</text></svg>');
        } catch (e) {
          event.target.style.backgroundColor = '#e5e7eb';
        }
      }
    }
  }
};
</script>

<style scoped>
.container {
  padding: 20px;
  background-color: var(--vtm-well);
  min-height: 100vh;
  width: -webkit-fill-available;
}

/* Header Section */
.header {
  display: flex;
  gap: 15px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.header-right {
  display: flex;
  gap: 10px;
  align-items: center;
  flex: 1;
  justify-content: flex-end;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-add {
  background-color: var(--vtm-mint);
  color: white;
  padding: 10px 24px;
}

.btn-add:hover {
  background-color: var(--vtm-mint-dim);
}

.search-input {
  padding: 10px 15px;
  border: 1px solid var(--vtm-line);
  border-radius: 4px;
  font-size: 14px;
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
  min-width: 300px;
}

.search-input::placeholder {
  color: var(--vtm-ink-3);
}

.search-input:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-search {
  background-color: var(--vtm-mint);
  color: white;
}

.btn-search:hover {
  background-color: var(--vtm-mint-dim);
}



/* Table Section */
.table-wrapper {
  background-color: var(--vtm-surface);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background-color: var(--vtm-surface-2);
  border-bottom: 1px solid var(--vtm-line);
}

.data-table th {
  padding: 15px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink-2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.data-table td {
  padding: 15px;
  border-bottom: 1px solid var(--vtm-line);
  font-size: 14px;
  color: var(--vtm-ink);
}

.data-table tbody tr:hover {
  background-color: var(--vtm-surface-2);
}

.group-name {
  font-weight: 600;
  color: var(--vtm-ink);
}

.notes {
  color: var(--vtm-ink-2);
  font-size: 13px;
}

.operations {
  display: flex;
  gap: 10px;
}

.btn-assign, .btn-reset, .btn-add-ads {
  background-color: var(--vtm-well);
  color: var(--vtm-ink);
  padding: 6px 12px;
  font-size: 12px;
  border: 0.5px solid var(--vtm-line-2);
  cursor: pointer;
  border-radius: 4px;
}

.btn-reset {
  height: 35px;
  font-size: 14px;
}

.btn-assign:hover {
  background-color: var(--vtm-well);
}

.btn-config {
  background-color: var(--vtm-warn);
  color: white;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;
}

.btn-config:hover {
  background-color: var(--vtm-warn);
}

.btn-delete {
  background-color: var(--vtm-crit);
  color: white;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;
}

.btn-delete:hover {
  background-color: var(--vtm-crit);
}

input[type="checkbox"] {
  cursor: pointer;
  width: 18px;
  height: 18px;
}

/* Pagination Section */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-info {
  font-size: 14px;
  color: var(--vtm-ink-2);
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  gap: 10px;
  align-items: center;
}

.btn-nav {
  background-color: var(--vtm-line);
  color: var(--vtm-ink);
  padding: 8px 12px;
  font-size: 16px;
  min-width: 40px;
  border-radius: 4px;
}

.btn-nav:hover:not(:disabled) {
  background-color: var(--vtm-well);
}

.btn-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-page-num {
  background-color: var(--vtm-mint);
  color: white;
  padding: 8px 16px;
  min-width: 40px;
  border-radius: 4px;
}

.btn-page-num.active {
  background-color: var(--vtm-mint);
}

.go-to {
  margin-left: 10px;
  font-size: 14px;
  color: var(--vtm-ink-2);
  margin-right: 5px;
}

.go-to-input {
  width: 40px;
  padding: 8px 10px;
  border: 1px solid var(--vtm-line);
  border-radius: 4px;
  font-size: 14px;
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
}

.go-to-input:focus {
  outline: none;
  border: 2px solid var(--vtm-mint);
}

/* Number input spinner styling */
.go-to-input::-webkit-outer-spin-button,
.go-to-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
}

.go-to-input[type=number] {
  -moz-appearance: textfield;
}

/* Responsive Design */
@media (max-width: 768px) {
  .header {
    flex-direction: column;
    gap: 10px;
  }

  .search-input {
    width: 100%;
  }

  .operations {
    flex-direction: column;
  }

  .pagination {
    flex-direction: column;
    gap: 15px;
  }

  .pagination-controls {
    width: 100%;
    justify-content: center;
  }

  .data-table {
    font-size: 12px;
  }

  .data-table th,
  .data-table td {
    padding: 10px;
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  z-index: 1000;
}

.modal-overlay-centered {
  justify-content: center;
  align-items: center;
  overscroll-behavior: contain;
}

.modal-content {
  background-color: var(--vtm-surface);
  width: 100%;
  max-width: 800px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.15);
  animation: slideInRight 0.3s ease-out;
}

/* Ads Configuration side panel - full viewport height (Mac Chrome safe) */
.ads-config-overlay {
  align-items: stretch;
  position: fixed;
  inset: 0;
}

.ads-config-modal-content {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  max-width: 900px;
  height: auto;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.ads-config-modal-content .modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--vtm-line);
}

.modal-title-container {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--vtm-ink);
}

.admin-badge {
  display: inline-block;
  background-color: var(--vtm-mint-soft);
  color: var(--vtm-mint-ink);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: var(--vtm-ink-2);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: var(--vtm-ink);
  background-color: var(--vtm-surface-2);
  border-radius: 4px;
}

/* Tabs */
.tabs-container {
  display: flex;
  gap: 10px;
  padding: 15px 20px;
  border-bottom: 1px solid var(--vtm-line);
  align-items: center;
}

.tab {
  padding: 10px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--vtm-ink-2);
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tab i {
  font-size: 16px;
}

.tab:hover {
  color: var(--vtm-ink);
}

.tab.active {
  color: var(--vtm-ink);
 border: 1px solid var(--vtm-line);
 
}

.btn-add-ads {
  margin-left: auto;
}


/* Modal Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: var(--vtm-surface);
}

.queue-info {
  font-size: 13px;
  color: var(--vtm-ink-2);
  margin-bottom: 16px;
  display: block;
}

.network-statistics {
  background-color: var(--vtm-mint-soft);
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  padding: 16px;
  margin-top: 20px;
}

.stats-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-mint-ink);
}

.stats-container {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}

.stat-item {
  flex: 1;
}

.stat-label {
  margin: 0;
  font-size: 12px;
  color: var(--vtm-ink-2);
  margin-bottom: 4px;
}

.stat-value {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--vtm-ink);
}

.stat-value.revenue {
  color: var(--vtm-ok);
}

.ads-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Ad Item Styling */
.ad-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 15px;
  border: 1px solid var(--vtm-line);
  border-radius: 6px;
  background-color: var(--vtm-surface);
  cursor: move;
  transition: all 0.2s ease;
}

.ad-item:hover {
  background-color: var(--vtm-surface-2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: var(--vtm-line-2);
}

.drag-handle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: var(--vtm-ink-3);
  cursor: grab;
  padding: 5px;
}

.dot-row {
  font-size: 16px;
  letter-spacing: -2px;
  line-height: 0.2;
}

.ad-item:hover .drag-handle {
  color: var(--vtm-ink-3);
}

.drag-handle:active {
  cursor: grabbing;
}

.ad-number {
  font-weight: 600;
  color: var(--vtm-ink-2);
  min-width: 30px;
  text-align: center;
  font-size: 14px;
}

.ad-image {
  width: 75px;
  height: 55px;
  border-radius: 4px;
  object-fit: cover;
  border: 1px solid var(--vtm-line);
  flex-shrink: 0;
}

.ad-image-video {
  display: block;
  pointer-events: none;
}

.ad-info {
  flex: 1;
  min-width: 180px;
}

.ad-name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
}

.ad-brand {
  margin: 3px 0 0 0;
  font-size: 12px;
  color: var(--vtm-ink-3);
}

.ad-dates {
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 250px;
}

.config-date-picker {
  width: 180px;
}
.config-date-picker.el-date-editor {
  width: 195px;
}
.config-date-range-picker {
  width: 100%;
  max-width: 360px;
}
.config-date-range-picker.el-date-editor {
  width: 100%;
  max-width: 360px;
}

.date-input {
  width: 90px;
  padding: 6px 8px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 4px;
  font-size: 12px;
  color: var(--vtm-ink);
  background-color: var(--vtm-surface);
  cursor: pointer;
}

.date-input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  color: var(--vtm-mint-ink);
}

.date-input:invalid {
  color: var(--vtm-ink-3);
}

.date-input::placeholder {
  color: var(--vtm-ink-3);
}

.date-input::-webkit-outer-spin-button,
.date-input::-webkit-inner-spin-button {
  display: none;
}

.date-input:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.date-separator {
  font-size: 12px;
  color: var(--vtm-ink-2);
  font-weight: 500;
  padding: 0 4px;
}

.btn-delete-ad {
  padding: 6px 10px;
  background-color: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--vtm-crit);
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-delete-ad i {
  font-size: 16px;
}

.btn-delete-ad:hover {
  background-color: var(--vtm-crit-soft);
  border-radius: 4px;
  color: var(--vtm-crit);
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--vtm-ink-2);
}

.empty-state p {
  margin: 0 0 15px 0;
  font-size: 14px;
}

.add-link {
  color: var(--vtm-mint-ink);
  text-decoration: none;
  font-weight: 500;
}

.add-link:hover {
  text-decoration: underline;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid var(--vtm-line);
  background-color: var(--vtm-surface-2);
}

.btn-cancel {
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
  border: 1px solid var(--vtm-line-2);
  padding: 10px 20px;
}

.btn-cancel:hover {
  background-color: var(--vtm-surface-2);
}

.btn-save {
  background-color: var(--vtm-mint);
  color: white;
  padding: 10px 20px;
}

.btn-save:hover {
  background-color: var(--vtm-mint-dim);
}

/* Drag and drop visual feedback */
.ad-item.dragging {
  opacity: 0.5;
  background-color: var(--vtm-mint-soft);
  border-color: var(--vtm-mint);
}

/* Responsive Modal */
@media (max-width: 1024px) {
  .modal-content {
    max-width: 95vw;
  }

  .ad-item {
    flex-wrap: wrap;
    gap: 8px;
  }

  .ad-dates {
    width: 100%;
    min-width: auto;
    order: 10;
  }

  .date-input {
    flex: 1;
  }
}

@media (max-width: 640px) {
  .modal-content {
    max-width: 100vw;
  }

  .tabs-container {
    flex-wrap: wrap;
  }

  .btn-add-ads {
    margin-left: 0;
    width: 100%;
  }

  .ad-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .ad-image {
    width: 100%;
    max-width: 100%;
  }

  .modal-footer {
    flex-direction: column-reverse;
  }

  .btn-cancel,
  .btn-save {
    width: 100%;
  }
}

/* Create Modal Styles */
.create-modal-content {
  width: 90%;
  max-width: 500px;
  height: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: fadeIn 0.3s ease-out;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.create-body {
  padding: 20px;
  flex: 1;
}

/* Assign Modal Styles */
.assign-modal-content {
  width: 90%;
  max-width: 700px;
  height: auto;
  max-height: 85vh;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: fadeIn 0.3s ease-out;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overscroll-behavior: none;
}

.assign-body {
  padding: 20px;
  flex: 1;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.assign-body::-webkit-scrollbar {
  display: none;
}

.form-group {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin-bottom: 8px;
}

.form-input,
.form-textarea {
  width: 95%;
  padding: 10px 12px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  color: var(--vtm-ink);
  background-color: var(--vtm-surface);
}


.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  color: var(--vtm-ink);
}

/* Assign Machines Section */
.assign-machines-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.assign-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.assign-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin: 0;
}

.machines-count {
  font-size: 13px;
  color: var(--vtm-ink-2);
}

/* Assign Tabs */
.assign-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--vtm-line);
  margin-bottom: 10px;
}

.assign-tab {
  padding: 10px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: var(--vtm-ink-2);
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.assign-tab i {
  font-size: 14px;
}

.assign-tab:hover {
  color: var(--vtm-ink);
}

.assign-tab.active {
  color: var(--vtm-mint-ink);
  border-bottom-color: var(--vtm-mint);
}

/* Machines and Groups Lists */
.assign-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.machines-list,
.groups-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.search-box {
  margin-bottom: 16px;
  flex-shrink: 0;
}

.search-input-modal {
  width: 95%;
  padding: 10px 12px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 4px;
  font-size: 13px;
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
}

.search-input-modal::placeholder {
  color: var(--vtm-ink-3);
}

.search-input-modal:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.machines-items,
.groups-items {
  flex: 1;
  overflow-y: auto;
  border: 1px solid var(--vtm-line);
  border-radius: 4px;
}

/* Custom scrollbar for machines and groups items */
.machines-items::-webkit-scrollbar,
.groups-items::-webkit-scrollbar {
  width: 8px;
}

.machines-items::-webkit-scrollbar-track,
.groups-items::-webkit-scrollbar-track {
  background: transparent;
}

.machines-items::-webkit-scrollbar-thumb,
.groups-items::-webkit-scrollbar-thumb {
  background: var(--vtm-line);
  border-radius: 4px;
}

.machines-items::-webkit-scrollbar-thumb:hover,
.groups-items::-webkit-scrollbar-thumb:hover {
  background: var(--vtm-ink-3);
}

/* Firefox scrollbar styling */
.machines-items,
.groups-items {
  scrollbar-color: var(--vtm-line) transparent;
  scrollbar-width: thin;
}

.machine-item,
.group-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid var(--vtm-line);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.machine-item:last-child,
.group-item:last-child {
  border-bottom: none;
}

.assign-loading,
.assign-empty {
  padding: 16px;
  text-align: center;
  color: var(--vtm-ink-2);
  font-size: 14px;
}

.config-loading {
  padding: 24px;
  text-align: center;
  color: var(--vtm-ink-2);
  font-size: 14px;
}

.machine-item:hover,
.group-item:hover {
  background-color: var(--vtm-surface-2);
}

.machine-checkbox,
.group-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: var(--vtm-mint);
}

.machine-label,
.group-label {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.machine-info,
.group-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.machine-id,
.group-name-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
}

.machine-address,
.group-date {
  font-size: 12px;
  color: var(--vtm-ink-2);
}

.machine-group,
.group-count {
  font-size: 12px;
  color: var(--vtm-ink-2);
  white-space: nowrap;
  margin-left: 16px;
}

.btn-cancel {
  background-color: var(--vtm-line);
  color: var(--vtm-ink);
}

.btn-cancel:hover {
  background-color: var(--vtm-well);
}

.btn-save {
  background-color: var(--vtm-mint);
  color: white;
}

.btn-save:hover {
  background-color: var(--vtm-mint-dim);
}

/* Select Ads Modal Styles */
.select-ads-modal-content {
  width: 90%;
  max-width: 1000px;
  height: auto;
  max-height: 80vh;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: fadeIn 0.3s ease-out;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-badge {
  display: inline-block;
  background-color: var(--vtm-mint-soft);
  color: var(--vtm-mint-ink);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 6px;
}

.modal-search-section {
  padding: 16px 20px;
  background-color: var(--vtm-surface-2);
  border-bottom: 1px solid var(--vtm-line);
}

.ads-search-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  font-size: 14px;
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
}

.ads-search-input::placeholder {
  color: var(--vtm-ink-3);
}

.ads-search-input:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.select-ads-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: var(--vtm-surface);
}

.ads-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
}

.ad-card {
  position: relative;
  border: 2px solid var(--vtm-line);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--vtm-surface);
  cursor: pointer;
  transition: all 0.3s ease;
}

.ad-card:hover {
  border-color: var(--vtm-mint);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.ad-card.selected {
  border-color: var(--vtm-mint);
  background-color: var(--vtm-mint-soft);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.ad-card-header {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
}

.ad-card-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--vtm-mint);
}

.ad-card-image {
  width: 100%;
  height: 100px;
  object-fit: cover;
  background-color: var(--vtm-surface-2);
}

.ad-card-video {
  display: block;
  pointer-events: none;
}

.ad-card-content {
  padding: 10px;
}

.ad-card-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--vtm-ink);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.ad-card-brand {
  margin: 4px 0 0 0;
  font-size: 12px;
  color: var(--vtm-ink-3);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.ads-loading-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: var(--vtm-ink-2);
  font-size: 14px;
}

.empty-ads-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: var(--vtm-ink-3);
  font-size: 14px;
}



.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--vtm-ink-2);
}

.empty-state p {
  margin: 0 0 15px 0;
  font-size: 14px;
}

.add-link {
  color: var(--vtm-mint-ink);
  text-decoration: none;
  font-weight: 500;
}

.add-link:hover {
  text-decoration: underline;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid var(--vtm-line);
  background-color: var(--vtm-surface-2);
}

.btn-cancel {
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
  border: 1px solid var(--vtm-line-2);
  padding: 10px 20px;
}

.btn-cancel:hover {
  background-color: var(--vtm-surface-2);
}

.btn-save {
  background-color: var(--vtm-mint);
  color: white;
  padding: 10px 20px;
}

.btn-save:hover {
  background-color: var(--vtm-mint-dim);
}

/* Drag and drop visual feedback */
.ad-item.dragging {
  opacity: 0.5;
  background-color: var(--vtm-mint-soft);
  border-color: var(--vtm-mint);
}

/* Responsive Modal */
@media (max-width: 1024px) {
  .modal-content {
    max-width: 95vw;
  }

  .ad-item {
    flex-wrap: wrap;
    gap: 8px;
  }

  .ad-dates {
    width: 100%;
    min-width: auto;
    order: 10;
  }

  .date-input {
    flex: 1;
  }
}

@media (max-width: 640px) {
  .modal-content {
    max-width: 100vw;
  }

  .tabs-container {
    flex-wrap: wrap;
  }

  .btn-add-ads {
    margin-left: 0;
    width: 100%;
  }

  .ad-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .ad-image {
    width: 100%;
    max-width: 100%;
  }

  .modal-footer {
    flex-direction: column-reverse;
  }

  .btn-cancel,
  .btn-save {
    width: 100%;
  }
}

/* Create Modal Styles */
.create-modal-content {
  width: 90%;
  max-width: 500px;
  height: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: fadeIn 0.3s ease-out;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.create-body {
  padding: 20px;
  flex: 1;
}

/* Assign Modal Styles */
.assign-modal-content {
  width: 90%;
  max-width: 700px;
  height: auto;
  max-height: 85vh;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: fadeIn 0.3s ease-out;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overscroll-behavior: none;
}

.assign-body {
  padding: 20px;
  flex: 1;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.assign-body::-webkit-scrollbar {
  display: none;
}

.form-group {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin-bottom: 8px;
}

.form-input,
.form-textarea {
  width: 95%;
  padding: 10px 12px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  color: var(--vtm-ink);
  background-color: var(--vtm-surface);
}


.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  color: var(--vtm-ink);
}

/* Assign Machines Section */
.assign-machines-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.assign-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.assign-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin: 0;
}

.machines-count {
  font-size: 13px;
  color: var(--vtm-ink-2);
}

/* Assign Tabs */
.assign-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--vtm-line);
  margin-bottom: 10px;
}

.assign-tab {
  padding: 10px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: var(--vtm-ink-2);
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.assign-tab i {
  font-size: 14px;
}

.assign-tab:hover {
  color: var(--vtm-ink);
}

.assign-tab.active {
  color: var(--vtm-mint-ink);
  border-bottom-color: var(--vtm-mint);
}

/* Machines and Groups Lists */
.assign-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.machines-list,
.groups-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.search-box {
  margin-bottom: 16px;
  flex-shrink: 0;
}

.search-input-modal {
  width: 95%;
  padding: 10px 12px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 4px;
  font-size: 13px;
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
}

.search-input-modal::placeholder {
  color: var(--vtm-ink-3);
}

.search-input-modal:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.machines-items,
.groups-items {
  flex: 1;
  overflow-y: auto;
  border: 1px solid var(--vtm-line);
  border-radius: 4px;
}

/* Custom scrollbar for machines and groups items */
.machines-items::-webkit-scrollbar,
.groups-items::-webkit-scrollbar {
  width: 8px;
}

.machines-items::-webkit-scrollbar-track,
.groups-items::-webkit-scrollbar-track {
  background: transparent;
}

.machines-items::-webkit-scrollbar-thumb,
.groups-items::-webkit-scrollbar-thumb {
  background: var(--vtm-line);
  border-radius: 4px;
}

.machines-items::-webkit-scrollbar-thumb:hover,
.groups-items::-webkit-scrollbar-thumb:hover {
  background: var(--vtm-ink-3);
}

/* Firefox scrollbar styling */
.machines-items,
.groups-items {
  scrollbar-color: var(--vtm-line) transparent;
  scrollbar-width: thin;
}

.machine-item,
.group-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid var(--vtm-line);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.machine-item:last-child,
.group-item:last-child {
  border-bottom: none;
}

.assign-loading,
.assign-empty {
  padding: 16px;
  text-align: center;
  color: var(--vtm-ink-2);
  font-size: 14px;
}

.config-loading {
  padding: 24px;
  text-align: center;
  color: var(--vtm-ink-2);
  font-size: 14px;
}

.machine-item:hover,
.group-item:hover {
  background-color: var(--vtm-surface-2);
}

.machine-checkbox,
.group-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: var(--vtm-mint);
}

.machine-label,
.group-label {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.machine-info,
.group-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.machine-id,
.group-name-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
}

.machine-address,
.group-date {
  font-size: 12px;
  color: var(--vtm-ink-2);
}

.machine-group,
.group-count {
  font-size: 12px;
  color: var(--vtm-ink-2);
  white-space: nowrap;
  margin-left: 16px;
}

.btn-cancel {
  background-color: var(--vtm-line);
  color: var(--vtm-ink);
}

.btn-cancel:hover {
  background-color: var(--vtm-well);
}

.btn-save {
  background-color: var(--vtm-mint);
  color: white;
}

.btn-save:hover {
  background-color: var(--vtm-mint-dim);
}

/* Select Ads Modal Styles */
.select-ads-modal-content {
  width: 90%;
  max-width: 1000px;
  height: auto;
  max-height: 80vh;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: fadeIn 0.3s ease-out;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-badge {
  display: inline-block;
  background-color: var(--vtm-mint-soft);
  color: var(--vtm-mint-ink);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 6px;
}

.modal-search-section {
  padding: 16px 20px;
  background-color: var(--vtm-surface-2);
  border-bottom: 1px solid var(--vtm-line);
}

.ads-search-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  font-size: 14px;
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
}

.ads-search-input::placeholder {
  color: var(--vtm-ink-3);
}

.ads-search-input:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.select-ads-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: var(--vtm-surface);
}

.ads-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
}

.ad-card {
  position: relative;
  border: 2px solid var(--vtm-line);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--vtm-surface);
  cursor: pointer;
  transition: all 0.3s ease;
}

.ad-card:hover {
  border-color: var(--vtm-mint);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.ad-card.selected {
  border-color: var(--vtm-mint);
  background-color: var(--vtm-mint-soft);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.ad-card-header {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
}

.ad-card-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--vtm-mint);
}

.ad-card-image {
  width: 100%;
  height: 100px;
  object-fit: cover;
  background-color: var(--vtm-surface-2);
}

.ad-card-video {
  display: block;
  pointer-events: none;
}

.ad-card-content {
  padding: 10px;
}

.ad-card-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--vtm-ink);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.ad-card-brand {
  margin: 4px 0 0 0;
  font-size: 12px;
  color: var(--vtm-ink-3);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.ads-loading-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: var(--vtm-ink-2);
  font-size: 14px;
}

.empty-ads-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: var(--vtm-ink-3);
  font-size: 14px;
}

.notes-toggle-group .form-label {
  margin-top: 10px;
}

.notes-toggle-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  color: var(--vtm-mint-ink);
  cursor: pointer;
  text-decoration: none;
  font-family: inherit;
}
.notes-toggle-link:hover {
  text-decoration: underline;
}
</style>
